/**
 * From the read-only plan's view to the Mapbox camera that shows it
 * (`spec-onboarding-3d-plan-plan-scene`, R3).
 *
 * ## The two sides
 *
 * The pane draws a plan point `p` (floor metres) at `pan + R(θ) · 50·zoom · (p.x, −p.y)` in the pane
 * (`planView.ts`). The 3D scene draws it through the chain launch and the live twin use, read from
 * the code rather than assumed:
 *
 * - `scalePoint`, the factory's point and `convertCoordLocalPoint` put it at scene
 *   `(x, z) = (p.x, −p.y)`: plan north is scene −z (`planSceneLayers.planPointToScene`);
 * - the floor's group turns it about Y and moves it (`floorGroupTransform`), and the twin's group
 *   turns it about Y by the orientation, as `Site` does;
 * - react-three-map places the scene at the origin as `T(origin) · Rx(−π/2) · S(s, −s, s)`, with
 *   `s = meterInMercatorCoordinateUnits()` there (`react-three-map` 1.0.1, `coordsToMatrix`), so a
 *   scene point lands at Mercator `origin + s · (x, z)`.
 *
 * Mapbox at pitch 0 draws Mercator `m` at `centre + R(−bearing) · 512·2^zoom · (m − m_centre)`.
 * Both sides carry one y-flip, so a rotation and a scale make them agree: the bearing is the angle
 * between the two linear maps, the zoom matches the scales, and the centre is the Mercator point
 * under the pane's centre.
 *
 * Every scale goes through Mapbox's own `MercatorCoordinate`, never an earth radius (R3.3).
 *
 * ## The plan-view pose
 *
 * `planViewPose` is where a map's plan-view pose is computed (`spec-onboarding-persistent-scene-plan-pose`,
 * R1): `planViewFit`, the one plan-view fit every plan map opens through
 * (`spec-onboarding-persistent-scene-editor-pose`, R1), turned into the camera above.
 *
 * ## Back again
 *
 * `planViewForCamera` is the exact inverse at pitch 0 (R6.2): a map that opens at a camera handed
 * back from another step takes it as its own plan view.
 */
import MapboxGl from 'mapbox-gl';
import {
  panePointToPlan,
  planPointToPane,
  type BoundingBox,
  type PlanView,
} from '@repo/front-end-utils';
import { PLAN_VIEW_PADDING_PX, clampViewZoom, planViewFit, type PaneRect } from '@repo/ui/planner';

type Point = { x: number; y: number };

/** CSS pixels per Mercator unit at zoom 0: Mapbox's tile size. */
const MAPBOX_TILE_SIZE = 512;
/** The pane's world pixels per metre (`metersToPixels`). */
const PIXELS_PER_METRE = 50;

/** Where the drawn floor sits: everything between a plan point and the map. */
export interface PlanSceneFrame {
  /** The scene's anchor: react-three-map's `latitude` / `longitude`. */
  origin: { latitude: number; longitude: number };
  /** The twin's orientation in degrees, turned about Y as `Site` turns it. */
  orientationDeg: number;
  /** The floor group's position and Y rotation, exactly as `floorGroupTransform` returns them. */
  floor: { position: [number, number, number]; rotation: [number, number, number] };
}

export interface PlanCamera {
  center: [number, number];
  zoom: number;
  bearing: number;
}

/** A Y rotation as three applies it, on the horizontal (x, z) plane. */
const rotateY = ({ x, y: z }: Point, radians: number): Point => {
  const c = Math.cos(radians);
  const s = Math.sin(radians);
  return { x: x * c + z * s, y: -x * s + z * c };
};

/** A plan point (floor metres) → the twin's horizontal scene plane, `(x, z)`. */
const planToTwinScene = (p: Point, frame: PlanSceneFrame): Point => {
  const [fx, , fz] = frame.floor.position;
  const onFloor = rotateY({ x: p.x, y: -p.y }, frame.floor.rotation[1]);
  return rotateY({ x: onFloor.x + fx, y: onFloor.y + fz }, (frame.orientationDeg * Math.PI) / 180);
};

const originMercator = (frame: PlanSceneFrame) =>
  MapboxGl.MercatorCoordinate.fromLngLat([frame.origin.longitude, frame.origin.latitude]);

/** One scene metre in Mercator units at the anchor: react-three-map's own scale. */
const mercatorPerMetre = (frame: PlanSceneFrame) =>
  originMercator(frame).meterInMercatorCoordinateUnits();

/** A plan point → its Mercator position, as react-three-map places the scene. */
const planPointToMercator = (p: Point, frame: PlanSceneFrame): Point => {
  const o = originMercator(frame);
  const s = mercatorPerMetre(frame);
  const scene = planToTwinScene(p, frame);
  return { x: o.x + s * scene.x, y: o.y + s * scene.y };
};

/** A plan point → the longitude and latitude the scene draws it at (the rig's `map.project` input). */
export const planPointToLngLat = (p: Point, frame: PlanSceneFrame): [number, number] => {
  const m = planPointToMercator(p, frame);
  const lngLat = new MapboxGl.MercatorCoordinate(m.x, m.y, 0).toLngLat();
  return [lngLat.lng, lngLat.lat];
};

const normaliseBearing = (deg: number) => {
  const d = ((deg % 360) + 360) % 360;
  return d > 180 ? d - 360 : d;
};

let warnedMirrored = false;

/** For tests: forget that the mirrored warning has been given. */
export function resetPlanCameraWarningsForTests(): void {
  warnedMirrored = false;
}

/**
 * The camera that shows `view` in a pane of `size`: `map.project` of any plan point on the drawn
 * floor lands where `planPointToPane` puts it (R3.2). The zoom is not clamped: the map clamps it at
 * its own maximum (R3.4).
 */
export const planCameraForView = (
  view: PlanView,
  size: { width: number; height: number },
  frame: PlanSceneFrame,
): PlanCamera => {
  // The scene's linear map, plan → (x, z), from the images of the plan's axes.
  const o = planToTwinScene({ x: 0, y: 0 }, frame);
  const ex = planToTwinScene({ x: 1, y: 0 }, frame);
  const ey = planToTwinScene({ x: 0, y: 1 }, frame);
  const lx = { x: ex.x - o.x, y: ex.y - o.y };
  const ly = { x: ey.x - o.x, y: ey.y - o.y };
  // The pane's linear map: plan x → R(θ)·(1, 0), plan y → R(θ)·(0, −1), each times 50·zoom.
  const theta = (view.rotation * Math.PI) / 180;
  const kx = { x: Math.cos(theta), y: Math.sin(theta) };
  const ky = { x: Math.sin(theta), y: -Math.cos(theta) };
  // Both must keep or both reverse handedness, or no bearing can make them agree.
  const sceneDet = lx.x * ly.y - lx.y * ly.x;
  const paneDet = kx.x * ky.y - kx.y * ky.x;
  if (Math.sign(sceneDet) !== Math.sign(paneDet) && !warnedMirrored) {
    warnedMirrored = true;
    console.warn('[PlanScene] the scene and the view disagree in handedness; the plan is mirrored');
  }
  const bearing = normaliseBearing(
    ((Math.atan2(lx.y, lx.x) - Math.atan2(kx.y, kx.x)) * 180) / Math.PI,
  );

  const s = mercatorPerMetre(frame);
  const pixelsPerMetre = PIXELS_PER_METRE * view.zoom;
  const zoom = Math.log2(pixelsPerMetre / (MAPBOX_TILE_SIZE * s));

  const centrePlan = panePointToPlan({ x: size.width / 2, y: size.height / 2 }, view);
  return { center: planPointToLngLat(centrePlan, frame), zoom, bearing };
};

/** Every plan-view fit's margin, in CSS pixels, from `@repo/ui/planner`; re-exported for existing imports. */
export { PLAN_VIEW_PADDING_PX };

/**
 * R1: the pose a map opens on the plan at: `bounds` (world pixels on the floor) fitted in a pane of
 * `size` at `rotation` by `planViewFit`, and the camera that shows that view through `frame`.
 */
export const planViewPose = (
  bounds: BoundingBox,
  rotation: number,
  size: { width: number; height: number },
  frame: PlanSceneFrame,
): { view: PlanView; camera: PlanCamera } => {
  const view = planViewFit(bounds, rotation, size);
  return { view, camera: planCameraForView(view, size, frame) };
};

/**
 * The pose of the washed plan behind the non-plan steps (`spec-onboarding-persistent-scene-wash`,
 * R5.1): `bounds` fitted by `planViewFit` inside `clearRect` (with the same `PLAN_VIEW_PADDING_PX`),
 * and the camera for a map of `pageSize`, so the plan is centred on the clear rectangle rather than
 * on the page. `clearRect` is in the map's own pixels, from its top-left.
 *
 * The fit's pan is moved by the rectangle's offset, which expresses the same view in the page's
 * pixels; `planCameraForView` then reads the page centre's plan point through the same inverses every
 * plan-view pose uses. No Mapbox `padding`, which the lock does not read.
 */
export const planViewPoseInRect = (
  bounds: BoundingBox,
  rotation: number,
  pageSize: { width: number; height: number },
  clearRect: PaneRect,
  frame: PlanSceneFrame,
): { view: PlanView; camera: PlanCamera } => {
  const fitted = planViewFit(bounds, rotation, {
    width: clearRect.width,
    height: clearRect.height,
  });
  const view: PlanView = {
    ...fitted,
    pan: { x: fitted.pan.x + clearRect.left, y: fitted.pan.y + clearRect.top },
  };
  return { view, camera: planCameraForView(view, pageSize, frame) };
};

/**
 * R6.2: the plan view a pane of `size` shows `camera` at, through `frame`: the inverse of
 * `planCameraForView` at pitch 0, so `planCameraForView(planViewForCamera(c, s, f), s, f)` is `c`.
 * The rotation is in [0, 360).
 */
export const planViewForCamera = (
  camera: PlanCamera,
  size: { width: number; height: number },
  frame: PlanSceneFrame,
): PlanView => {
  // The scene's affine map, plan → (x, z): its origin and the images of the plan's axes.
  const o = planToTwinScene({ x: 0, y: 0 }, frame);
  const ex = planToTwinScene({ x: 1, y: 0 }, frame);
  const ey = planToTwinScene({ x: 0, y: 1 }, frame);
  const lx = { x: ex.x - o.x, y: ex.y - o.y };
  const ly = { x: ey.x - o.x, y: ey.y - o.y };
  // planCameraForView's bearing is atan2(lx) − θ, so θ is atan2(lx) − bearing. Rounded to a
  // millionth of a degree: the round trip through Mapbox's radians leaves noise (89.99999999999999),
  // which the editor's rotation label and its announcement would show.
  const turned =
    Math.round(((Math.atan2(lx.y, lx.x) * 180) / Math.PI - camera.bearing) * 1e6) / 1e6;
  const rotation = ((turned % 360) + 360) % 360;

  const s = mercatorPerMetre(frame);
  const zoom = (MAPBOX_TILE_SIZE * s * 2 ** camera.zoom) / PIXELS_PER_METRE;

  // The plan point under the camera's centre: solve o + p.x·lx + p.y·ly = (m − origin) / s.
  const origin = originMercator(frame);
  const m = MapboxGl.MercatorCoordinate.fromLngLat(camera.center);
  const scene = { x: (m.x - origin.x) / s - o.x, y: (m.y - origin.y) / s - o.y };
  const det = lx.x * ly.y - lx.y * ly.x;
  const centrePlan = {
    x: (scene.x * ly.y - scene.y * ly.x) / det,
    y: (lx.x * scene.y - lx.y * scene.x) / det,
  };
  // The pan that draws that point at the pane's centre.
  const atZero = planPointToPane(centrePlan, { zoom, rotation, pan: { x: 0, y: 0 } });
  return { zoom, rotation, pan: { x: size.width / 2 - atZero.x, y: size.height / 2 - atZero.y } };
};

/**
 * R6.1: the camera that draws `camera.center` at the viewport point `at`, in a pane whose viewport
 * box is `pane`. When `at` lies outside the pane, `camera` is returned as it is: its centre is drawn at
 * the pane's centre. Mapbox draws Mercator `m` at `centre + R(−bearing) · 512·2^zoom · (m − m_centre)`,
 * so the centre moves by `R(bearing) · Δ / (512·2^zoom)`, `Δ` the point's offset from the pane's centre.
 */
export const cameraWithPointAt = (
  camera: PlanCamera,
  at: { x: number; y: number },
  pane: { left: number; top: number; width: number; height: number },
): PlanCamera => {
  const inside =
    at.x >= pane.left &&
    at.x <= pane.left + pane.width &&
    at.y >= pane.top &&
    at.y <= pane.top + pane.height;
  if (!inside) return camera;
  const dx = at.x - (pane.left + pane.width / 2);
  const dy = at.y - (pane.top + pane.height / 2);
  const k = MAPBOX_TILE_SIZE * 2 ** camera.zoom;
  const b = (camera.bearing * Math.PI) / 180;
  const c = Math.cos(b);
  const sn = Math.sin(b);
  const m = MapboxGl.MercatorCoordinate.fromLngLat(camera.center);
  const centre = new MapboxGl.MercatorCoordinate(
    m.x - (c * dx - sn * dy) / k,
    m.y - (sn * dx + c * dy) / k,
    0,
  ).toLngLat();
  return { ...camera, center: [centre.lng, centre.lat] };
};

/**
 * The frame the camera and the pick read the view in (`spec-onboarding-3d-plan-edit-pdf-stack`,
 * R3.3). On Edit floor the canvas model's view is the floor's own frame: the placement's offset
 * dropped and its turn folded into the view, so `frame` stands. In Build floors the view is the
 * world, as the align stage is, and the floor's group (which carries the placement) puts each
 * floor point back where the placement says: so the frame takes the placement out, keeping the
 * floor's altitude.
 */
export const planViewFrame = (aligning: boolean, frame: PlanSceneFrame): PlanSceneFrame =>
  aligning
    ? { ...frame, floor: { position: [0, frame.floor.position[1], 0], rotation: [0, 0, 0] } }
    : frame;

/**
 * R6.1, R6.2: the plan view a map opens at for a handed-back camera, in a pane whose viewport box is
 * `pane`: the handed centre drawn at the handed `at` (or the pane's centre when `at` is outside it),
 * the zoom held within `zoomRange` about that same point, so a clamp never moves it.
 */
export const handedPlanView = (
  handed: PlanCamera & { at: Point },
  pane: PaneRect,
  frame: PlanSceneFrame,
  zoomRange: { min: number; max: number },
): PlanView => {
  const size = { width: pane.width, height: pane.height };
  const view = planViewForCamera(cameraWithPointAt(handed, handed.at, pane), size, frame);
  const local = { x: handed.at.x - pane.left, y: handed.at.y - pane.top };
  const inside = local.x >= 0 && local.x <= pane.width && local.y >= 0 && local.y <= pane.height;
  return clampViewZoom(view, size, zoomRange.min, zoomRange.max, inside ? local : undefined);
};
